import { Variant, type LoadWidgetProps, type VariantOptionOverrides } from '@asurion-core/widget-client/lib/package';

export type DeepLink = { label: string; message: string };

export type WidgetIdOption = {
  id: string;
  parentElementId?: string;
  showStateDebugger?: boolean;
  flowSystemId?: string;
  context?: Record<string, unknown>;
  variantOptionOverrides?: VariantOptionOverrides;
  deepLinks?: DeepLink[];
};

export type VariantPreset = {
  label: string;
  parentElementId?: string;
};

const talkToExpertDeepLink: DeepLink = { label: 'Talk to an expert', message: 'Talk to an expert' };

export const widgetIdMap: Record<string, WidgetIdOption[]> = {
  production: [
    { id: 'asurion_tech_tips', deepLinks: [talkToExpertDeepLink] },
    {
      id: 'vz_embedded',
      deepLinks: [
        { label: 'Get help setting up my device', message: 'Get help setting up my device' },
        { label: 'Get a guided tour of my device', message: 'Get a guided tour of my device' },
      ],
    },
    { id: 'att_onboarding' },
  ],
  development: [
    { id: 'chai-features', showStateDebugger: true },
    { id: 'si-verizon', showStateDebugger: true, deepLinks: [talkToExpertDeepLink] },
    { id: 'inline-scheduler', showStateDebugger: true },
  ],
  qa: [
    { id: 'chai-features', showStateDebugger: true },
    { id: 'simplr_twilio_flex', showStateDebugger: true },
    { id: 'mcafee_stg', showStateDebugger: true },
    { id: 'solostove_qa_clone', showStateDebugger: true },
    { id: 'simplr_qa_hum_widget', showStateDebugger: true },
    { id: 'princess_polly_qa', showStateDebugger: true },
  ],
};

export const variantPresets: Record<Variant, VariantPreset> = {
  [Variant.FLOATING_ACTION_BUTTON]: { label: 'Floating Action Button' },
  [Variant.FLOATING_ACTION_CARD]: { label: 'Floating Action Card' },
  [Variant.FLOATING_INPUT_CARD]: { label: 'Floating Input Card' },
  [Variant.EMBEDDED]: { label: 'Embedded', parentElementId: 'embedded-widget-placeholder' },
};

export const orderedVariants: Variant[] = [
  Variant.FLOATING_ACTION_BUTTON,
  Variant.FLOATING_ACTION_CARD,
  Variant.FLOATING_INPUT_CARD,
  Variant.EMBEDDED,
];

const rawEnv = process.env.NX_PUBLIC_APP_ENV;
const envKey = rawEnv === 'aqa' ? 'qa' : rawEnv === 'uat' ? 'production' : (rawEnv ?? 'production');

export const widgetIdsForEnv: WidgetIdOption[] = widgetIdMap[envKey] ?? widgetIdMap.production;

export type BuiltLoadParams = Pick<
  LoadWidgetProps,
  'id' | 'parentElementId' | 'showStateDebugger' | 'flowSystemId' | 'context' | 'variantOptionOverrides'
> & { variant: Variant };

export const buildLoadParams = (option: WidgetIdOption, variant: Variant): BuiltLoadParams => {
  const preset = variantPresets[variant];
  const parentElementId = option.parentElementId ?? preset.parentElementId;
  return {
    id: option.id,
    variant,
    ...(parentElementId ? { parentElementId } : {}),
    ...(option.showStateDebugger !== undefined ? { showStateDebugger: option.showStateDebugger } : {}),
    ...(option.flowSystemId ? { flowSystemId: option.flowSystemId } : {}),
    ...(option.context ? { context: option.context } : {}),
    ...(option.variantOptionOverrides ? { variantOptionOverrides: option.variantOptionOverrides } : {}),
  };
};

export const findWidgetIdOption = (id: string): WidgetIdOption | undefined =>
  widgetIdsForEnv.find((option) => option.id === id);
